import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_CLIP_SECONDS = 60;
const MIN_CLIP_SECONDS = 0.5;

const pickMimeType = () =>
  ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find(
    (t) => window.MediaRecorder?.isTypeSupported?.(t)
  ) || '';

export const extForMime = (mime = '') => {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
};

// permission: unknown | granted | denied
export function useVoiceRecorder({ onClip, onError, maxSeconds = MAX_CLIP_SECONDS }) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [permission, setPermission] = useState('unknown');

  const callbacks = useRef({ onClip, onError });
  callbacks.current = { onClip, onError };

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const limitRef = useRef(null);
  const pressedRef = useRef(false);

  const getStream = useCallback(async () => {
    if (streamRef.current?.active) return streamRef.current;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      throw new Error('unsupported');
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    streamRef.current = stream;
    setPermission('granted');
    return stream;
  }, []);

  const stop = useCallback(() => {
    pressedRef.current = false;
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }, []);

  const start = useCallback(async () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') return;
    pressedRef.current = true;

    let stream;
    try {
      stream = await getStream();
    } catch (err) {
      pressedRef.current = false;
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') setPermission('denied');
      callbacks.current.onError?.(err);
      return;
    }
    if (!pressedRef.current) return; // released while the permission prompt was open

    const mimeType = pickMimeType();
    let rec;
    try {
      rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch (err) {
      callbacks.current.onError?.(err);
      return;
    }

    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      clearInterval(tickRef.current);
      clearTimeout(limitRef.current);
      const duration = (Date.now() - startedAtRef.current) / 1000;
      const type = rec.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      recorderRef.current = null;
      setIsRecording(false);
      setElapsed(0);
      if (duration < MIN_CLIP_SECONDS || !blob.size) {
        callbacks.current.onError?.(new Error('too_short'));
        return;
      }
      callbacks.current.onClip?.({ blob, duration: Math.round(duration * 10) / 10, mimeType: type });
    };

    recorderRef.current = rec;
    startedAtRef.current = Date.now();
    rec.start(250);
    setIsRecording(true);
    setElapsed(0);
    tickRef.current = setInterval(() => setElapsed((Date.now() - startedAtRef.current) / 1000), 200);
    limitRef.current = setTimeout(stop, maxSeconds * 1000);
  }, [getStream, stop, maxSeconds]);

  useEffect(
    () => () => {
      clearInterval(tickRef.current);
      clearTimeout(limitRef.current);
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') rec.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    },
    []
  );

  return { start, stop, isRecording, elapsed, permission, maxSeconds };
}
